import React from 'react';
import { connect } from 'react-redux';

import { User } from '@paragon/contract-library';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { isUserLoggedIn } from '../../helpers';
import { LoginDataSliceState } from '../../store/logInDataSlice';
import { FlashContext } from './LoginScreen';
import { AuthorizeApiAccessPanel } from './panels/AuthorizeApiAccessPanel';
import { EmailAndPasswordEntry } from './panels/EmailAndPasswordEntry';
import { ResetPassword } from './panels/ResetPassword';
import { SignUp } from './panels/SignUp';
import { VerifyAccount } from './panels/VerifyAccount';

export type LogInPanel = 'logIn' | 'signUp' | 'verifyAccount' | 'resetPassword' | 'authorizeApi';

export function isLoginPanel(panel: string): panel is LogInPanel {
  return ['logIn', 'signUp', 'verifyAccount', 'resetPassword', 'authorizeApi'].includes(panel);
}

export interface LoginScreenContentsProps {
  readonly verifyAccountEmail?: string;

  readonly apiAuthorizationCode?: string;

  readonly signUp?: boolean;

  readonly onLogIn?: (user: User) => void;

  readonly onAccountCreated?: () => void;

  // If provided, this will be called instead of the default behavior for the 'Forgot Password' link
  readonly onResetPassword?: (initialEmail?: string) => void;

  readonly setCancelDisabled?: (disabled: boolean) => void;

  readonly additionalContent?: (panel: LogInPanel) => React.ReactNode;
}

type StateProps = ReturnType<typeof mapStateToProps>;

type Props = LoginScreenContentsProps & StateProps;

interface State {
  readonly panel: LogInPanel;

  readonly initialEmail?: string;
  readonly initialResetCode?: string;
}

/**
 * This is the core LogIn component, which renders the appropriate panel for the user's current account-related action.
 */
class StandardLoginScreenContents extends React.Component<Props, State> {
  static contextType = FlashContext;
  declare context: React.ContextType<typeof FlashContext>;

  constructor(props: Props) {
    super(props);

    const searchParams = new URLSearchParams(window.location.search);

    const initialPanel = searchParams.get('loginPanel');
    const initialResetCode = searchParams.get('resetCode');

    if (initialResetCode && initialPanel === 'resetPassword') {
      this.state = { panel: 'resetPassword', initialResetCode };
      return;
    }

    if (initialPanel && isLoginPanel(initialPanel)) {
      this.state = { panel: initialPanel };
      return;
    }

    this.state = {
      panel:
        props.loggedIn && props.apiAuthorizationCode
          ? 'authorizeApi'
          : props.verifyAccountEmail
            ? 'verifyAccount'
            : props.signUp
              ? 'signUp'
              : 'logIn',
    };
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    // Transitioning between sub-panels should clear the flash message.
    if (prevState.panel !== this.state.panel) {
      this.context.clear();
    }
  }

  render() {
    if (this.props.needsPasswordChange) {
      return <ResetPassword />;
    }

    return (
      <>
        {this.renderPanel()}
        {this.props.additionalContent ? this.props.additionalContent(this.state.panel) : null}
      </>
    );
  }

  renderPanel() {
    switch (this.state.panel) {
      case 'logIn':
        return (
          <EmailAndPasswordEntry
            initialEmail={this.state.initialEmail}
            onLogin={(user: User) => {
              if (this.props.onLogIn) this.props.onLogIn(user);
              if (this.props.apiAuthorizationCode) this.setState({ panel: 'authorizeApi' });
            }}
            onForgotPassword={(email) => {
              if (this.props.onResetPassword) this.props.onResetPassword(email);
              else this.setState({ panel: 'resetPassword', initialEmail: email });
            }}
            onLoginFailed={() => {
              this.context.set('warning', 'Login failed! Please try again.');
            }}
            onSignupClick={(email?: string) =>
              this.setState((oldState) => {
                if (email && oldState.initialEmail !== email) {
                  return { panel: 'signUp', initialEmail: email };
                } else {
                  return { panel: 'signUp' };
                }
              })
            }
          />
        );

      case 'signUp':
        return (
          <SignUp
            initialEmail={this.state.initialEmail}
            onSuccess={(email) => {
              this.setState({ panel: 'verifyAccount', initialEmail: email });
            }}
          />
        );

      case 'verifyAccount':
        return (
          <VerifyAccount
            email={(this.props.verifyAccountEmail ?? this.state.initialEmail)!}
            onAccountCreated={this.props.onAccountCreated}
            setCancelDisabled={this.props.setCancelDisabled}
          />
        );

      case 'resetPassword':
        return (
          <ResetPassword
            initialEmail={this.state.initialEmail}
            initialResetCode={this.state.initialResetCode}
            onSuccessfulReset={(initialEmail) => {
              this.setState({ panel: 'logIn', initialEmail }, () =>
                // The flash message must be set separately from the above panel change, because switching panels
                //   dismisses the active flash message.
                this.context.set('info', 'Password successfully reset!  Please log in with your new password.'),
              );
            }}
          />
        );

      case 'authorizeApi':
        return <AuthorizeApiAccessPanel authorizationCode={this.props.apiAuthorizationCode!} />;

      default:
        assertExhaustiveSwitch(this.state.panel);
    }
  }
}

const mapStateToProps = (state: LoginDataSliceState) => ({
  loggedIn: isUserLoggedIn(state),
  needsPasswordChange: state.logInData.needsPasswordChange,
});

export default connect(mapStateToProps)(StandardLoginScreenContents);
